const normalizeUrl = (url: string): string => url.replace(/\/$/, "");

const viteEnv = import.meta.env ?? {};

const inferWebAppUrl = (): string => {
  const apiHost = viteEnv.VITE_API_HOST;

  if (apiHost) {
    try {
      const apiUrl = new URL(apiHost);

      if (apiUrl.hostname === "api.prismancy.com") {
        return `${apiUrl.protocol}//app.prismancy.com`;
      }

      if (apiUrl.hostname.endsWith("-api.prismancy.com")) {
        return `${apiUrl.protocol}//${apiUrl.hostname.replace(/-api\.prismancy\.com$/, "-app.prismancy.com")}`;
      }
    } catch {
      // Fall through to the browser/prod fallback.
    }
  }

  if (
    typeof window !== "undefined" &&
    window.location.protocol.startsWith("http")
  ) {
    return window.location.origin;
  }

  return "https://app.prismancy.com";
};

export const API_ENDPOINT: string = viteEnv.VITE_API_HOST;
export const WEB_APP_URL: string = normalizeUrl(
  viteEnv.VITE_WEB_APP_URL || inferWebAppUrl(),
);
export const CHROME_EXTENSION_ID: string =
  viteEnv.VITE_CHROME_EXTENSION_ID || "ajkiofgaadilfigclomplkocjfdkljbb";
export const CHROME_EXTENSION_MIN_VERSION: string =
  viteEnv.VITE_CHROME_EXTENSION_MIN_VERSION || "";
export const CHROME_WEBSTORE_URL: string =
  viteEnv.VITE_CHROME_WEBSTORE_URL ||
  `https://chromewebstore.google.com/detail/prismancy/${CHROME_EXTENSION_ID}`;
export const APP_LOGIN_ORIGIN: string = normalizeUrl(
  viteEnv.VITE_APP_LOGIN_ORIGIN || "https://app.prismancy.com",
);
export const LEGACY_PLATFORM_ORIGIN: string = normalizeUrl(
  viteEnv.VITE_LEGACY_PLATFORM_ORIGIN || "https://webapp.prismancy.com",
);
export const LEGACY_DOMAIN_MIGRATION_ENABLED: boolean = ["1", "true", "on"].includes(
  String(viteEnv.VITE_LEGACY_DOMAIN_MIGRATION_ENABLED ?? "true")
    .trim()
    .toLowerCase(),
);
export const API_REQUEST_TIMEOUT: number = 30000;

export const ROUTE_PROGRESS_ENABLED: boolean = ["1", "true", "on"].includes(
  String(viteEnv.VITE_ROUTE_PROGRESS_ENABLED ?? "false").trim().toLowerCase(),
);

export const REPORT_MINIMUM_SESSION_COUNT: number = 100;

export const EXPERIMENT_VARIANT_LIMIT: number = 10;

export const AUTH_TOKEN_KEY: string = "prismancy_token";
export const USER_DETAILS_KEY: string = "prismancy_user_details";
export const SELECTED_WORKSPACE_KEY: string = "prismancy_workspace";
export const LIST_CURRENCIES_KEY: string = "prismancy_list_currencies";
export const LIST_COUNTRIES_KEY: string = "prismancy_list_countries";
export const LIST_TIMEZONES_KEY: string = "prismancy_list_timezones";
export const LIST_EXPERIMENTS_KEY: string = "prismancy_list_experiments";
export const LIST_OBJECTIVES_KEY: string = "prismancy_list_objectives";
export const CURRENT_EXPERIMENT_KEY: string = "prismancy_current_experiment";
export const CURRENT_REPORT_KEY: string = "prismancy_current_report";
export const CURRENT_SESSION_REPORT_KEY: string =
  "prismancy_current_session_report";
export const LATEST_REPORT_SNAPSHOT_KEY: string =
  "prismancy_latest_report_snapshot";
export const LIST_WORKSPACE_MEMBERS: string =
  "prismancy_list_workspace_members";
export const LIST_WORKSPACE_INVITES: string =
  "prismancy_list_workspace_invites";
export const LIST_WORKSPACE_DOMAINS: string =
  "prismancy_list_workspace_domains";
export const LIST_USER_WORKSPACES_KEY: string =
  "prismancy_list_user_workspaces";
export const LIST_USER_INVITATIONS: string = "prismancy_list_user_invitations";
export const LIST_VARIANTS_KEY: string = "prismancy_list_variants";
export const CURRENT_VARIANT_KEY: string = "prismancy_current_variant";
export const VARIANT_PREVIEW_LINK_KEY: string =
  "prismancy_variant_preview_link";
export const LIST_CHANGES_KEY: string = "prismancy_list_changes";
export const CURRENT_CHANGE_KEY: string = "prismancy_current_change";
export const DOMAIN_SNIPPET_KEY: string = "prismancy_domain_snippet";
export const PIXEL_SNIPPET_KEY: string = "prismancy_pixel_snippet";
export const GA4_SETTINGS: string = "prismancy_ga4_settings";
export const LIST_RISK_PROFILES: string = "prismancy_list_risk_profiles";
export const LIST_MDE_SENSITIVITIES: string =
  "prismancy_list_mde_sensitivities";
export const ACTIVITY_LOG_KEY: string = "prismancy_activity_log";
export const SHOPIFY_SYNC_STATUS_KEY: string = "prismancy_shopify_sync_status";
export const WORKSPACE_CAPABILITIES_KEY: string =
  "prismancy_workspace_capabilities";

// for local storage
export const LIST_EXPERIMENTS_LAST_STATUS: string =
  "prismancy_list_experiments_last_status";

export const MODAL_ROOT_ID = "modal-root";

export const BILLING_STATUS_KEY: string = "prismancy_billing_status";
export const BILLING_PLANS_KEY: string = "prismancy_billing_plans";
export const BILLING_INVOICES_KEY: string = "prismancy_billing_invoices";
export const TARGETING_RULES_KEY: string = "prismancy_targeting_rules";

export const DEFAULT_INPUT_MAX_LENGTH: number = 255;
export const INPUT_MAX_LENGTH_CAP: number = 10000;

export const HELP_TEXT_FONT_SIZE: string = "11px";

export const FIELD_LABEL_GAP: string = "gap-1.5";

export const VIEW_PICKER_MAX_WIDTH: string = "400px";
export const VIEW_PICKER_SHEET_MAX_WIDTH: string = "600px";

export const CHARACTER_LIMIT_TOAST_DURATION: number = 4000;
